import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/axios';
import { useToast } from '../components/common/Toast';

const AuthContext = createContext();

// Create a QueryClient instance for cache clearing
let queryClient = null;

export const setQueryClient = (client) => {
  queryClient = client;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ✅ Inner component that uses useNavigate hook
function AuthProviderContent({ children }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await api.get('/auth/profile');
        setUser(response.data.user);
      } catch (error) {
        // Failed to fetch user profile
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      const { token, user: userData } = response.data;
      
      // ✅ Step 1: Update auth state BEFORE clearing cache
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(token);
      setUser(userData);
      
      // ✅ Step 2: Update axios Authorization header immediately
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // ✅ Step 3: Clear old React Query cache for next user's context
      if (queryClient) {
        queryClient.clear();
        queryClient.cancelQueries();
      }
      
      toast.success('تم تسجيل الدخول بنجاح');
      
      // ✅ Step 4: Navigate based on role using React Router
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 200);
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'فشل تسجيل الدخول';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { token, user: newUser } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(newUser));
      setToken(token);
      setUser(newUser);
      
      toast.success('تم إنشاء الحساب بنجاح، تم إرسال رابط تفعيل إلى بريدك الإلكتروني.');
      
      // Navigate based on role using React Router
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 200);
      
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      const message = data?.error || 'فشل إنشاء الحساب';
      return { success: false, error: data || { error: message } };
    }
  };

  const logout = async () => {
    try {
      // ✅ Step 1: Call API logout endpoint to notify server
      try {
        await api.post('/auth/logout');
      } catch (error) {
        // Continue with logout even if API call fails
        console.warn('API logout failed:', error.message);
      }

      // ✅ Step 2: Clear React Query cache BEFORE clearing state
      // This prevents any queries from using old cached data
      if (queryClient) {
        queryClient.clear();
        queryClient.cancelQueries();
      }

      // ✅ Step 3: Clear all localStorage items related to authentication
      localStorage.clear(); // Clear everything to be safe
      
      // ✅ Step 4: Clear axios Authorization header and default headers
      delete api.defaults.headers.common['Authorization'];
      api.defaults.headers.common['Authorization'] = '';
      
      // ✅ Step 5: Clear all cookies (including session_token)
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
      });

      // ✅ Step 6: Update component state
      setToken(null);
      setUser(null);
      
      // ✅ Step 7: Show success message
      toast.success('تم تسجيل الخروج بنجاح');
      
      // ✅ Step 8: Navigate to login page using React Router
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 500);
      
    } catch (error) {
      console.error('Logout error:', error);
      
      // Even if there's an error, still clear local state
      localStorage.clear();
      delete api.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      
      toast.error('حدث خطأ أثناء تسجيل الخروج');
      
      // Still redirect to login
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1000);
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isTeacher: user?.role === 'teacher',
    isStudent: user?.role === 'student',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ✅ Main AuthProvider component
export const AuthProvider = ({ children }) => {
  return (
    <AuthProviderContent>
      {children}
    </AuthProviderContent>
  );
};
